import { useEffect, useMemo, useState } from "react";
import { DataTable } from "@/components/ui/table/data-table";
import { ColumnDef } from "@tanstack/react-table";
import { displayValue, displayDate, formatToPKR } from "@/lib/helper";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import { InvoiceDetails } from "@/types/invoice";
import { DataTableRowActions } from "./data-table-row-actions";
import { SearchCriteriaType } from "@/types";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import { useParams } from "react-router-dom";
import { useGetInvoicesQuery } from "@/services/invoiceApi";
import LinkedMembers from "./linked-members";
import { MemberTableDatatypes } from "@/types/member";
import { dummyTransactionData } from "@/lib/constants/invoice";
import useTableData from "@/components/ui/table/data-table-logic";
import { InvoiceStatus } from "@/types/enums";
import { Badge } from "@/components/ui/badge";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

type BusinessDetailProps = {
  memberInfo: MemberTableDatatypes | undefined;
};

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
};
const InvoiceTableForMember = ({ memberInfo }: BusinessDetailProps) => {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;
  const member = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).member ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();
  const { memberId } = useParams();

  const { searchCriteria, query, toggleSortOrder, setSearchCriteria } =
    useTableData(initialValue);
  const [selectedInvoice, setSelectedInvoice] = useState<
    InvoiceDetails | undefined
  >(undefined);

  const {
    data: memberInvoiceInfo,
    isLoading,
    refetch,
  } = useGetInvoicesQuery(
    { query: `member_id=${memberId}&${query}`, org_id: orgId },
    {
      skip: !memberId,
    }
  );

  const memberInvoiceInfoTableData = useMemo(() => {
    return Array.isArray(memberInvoiceInfo?.data) ? memberInvoiceInfo.data : [];
  }, [memberInvoiceInfo]);

  console.log({ memberInvoiceInfo, dummyTransactionData });

  const [openLinkedMembers, setOpenLinkedMembers] = useState(false);

  useEffect(() => {
    if (selectedInvoice) {
      setSelectedInvoice((prev) =>
        memberInvoiceInfoTableData.find(
          (invoice) => invoice.member_id == prev?.member_id
        )
      );
    }
  }, [refetch]);

  const columns: ColumnDef<InvoiceDetails>[] = [
    {
      accessorKey: "date",
      meta: "transaction date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Date"
          sortKey="payment_date"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayDate(row?.original.created_at)}
          </div>
        );
      },
    },
    {
      accessorKey: "receipt_number",
      meta: "Receipt No",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Receipt No"
          sortKey="receipt_number"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row?.original.invoice_number)}
          </div>
        );
      },
    },
    {
      accessorKey: "quantity",
      meta: "quantity",
      header: ({ column }) => (
        <DataTableColumnHeader
          searchCriteria={searchCriteria} // Pass current search criteria
          column={column}
          title="Quantity"
          sortKey="quantity"
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row?.original.total_quantity)}
          </div>
        );
      },
    },
    {
      accessorKey: "price",
      meta: "price",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title=" Price"
          sortKey="total"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {formatToPKR(row?.original.total)}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      meta: "status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const statusStyles: Record<InvoiceStatus, string> = {
          [InvoiceStatus.Pending]: "bg-yellow-400 text-yellow-950",
          [InvoiceStatus.Paid]: "bg-primary text-white",
          [InvoiceStatus.Cancelled]: "bg-red-600 text-white",
          [InvoiceStatus.Refunded]: "bg-gray-300 text-black",
          [InvoiceStatus.Processing]: "bg-blue-400 text-white", // Default styling for processing
        };

        const status = row?.original.status;
        const statusStyle = statusStyles[status] || "";

        return (
          <Badge className={`w-fit ${statusStyle}`}>
            {status}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const pay_invoice_access = (() => {
          try {
            return (
              JSON.parse(localStorage.getItem("accessLevels") as string)
                .hide_member_invoice_payment ?? "no_access"
            );
          } catch {
            return "no_access";
          }
        })();

        return row.original.status != InvoiceStatus.Refunded && (
          <DataTableRowActions
            access={member}
            payAccess={pay_invoice_access}
            data={row?.original}
            openLinkedMembers={setOpenLinkedMembers}
            setSelectedInvoice={setSelectedInvoice}
          />
        );
      },
    },
  ];

  const totalRecords = memberInvoiceInfo?.filtered_counts || 0;
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  return (
    <>
      {/* search bar */}
      <div className="m-2 ">
        <DataTable
          columns={columns}
          data={memberInvoiceInfoTableData}
          isLoading={false}
        />
      </div>
      {/* pagination */}
      {memberInvoiceInfoTableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <LinkedMembers
        isOpen={openLinkedMembers}
        setOpen={setOpenLinkedMembers}
        invoiceId={selectedInvoice?.id as number}
      />
    </>
  );
};

export default InvoiceTableForMember;
